import React, { Component } from 'react';
import { api } from '../utils/api';
import toast from 'react-hot-toast';
import {
  Plus, Search, Edit2, Trash2, RefreshCw, X, Save, Upload, FileSpreadsheet, CheckCircle, AlertTriangle,
  Hotel, ToggleLeft, ToggleRight, Shield, MapPin,
  Phone, Mail, User, Building2, ChevronDown, ChevronUp, Download, BedDouble
} from 'lucide-react';

const INDIAN_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa',
  'Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala',
  'Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland',
  'Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura',
  'Uttar Pradesh','Uttarakhand','West Bengal','Delhi','Jammu & Kashmir',
  'Ladakh','Chandigarh','Puducherry','Lakshadweep',
  'Andaman & Nicobar Islands','Dadra & Nagar Haveli','Daman & Diu'
];

const VENDOR_TYPES = ['Hotel','Airline','Event','Other'];
const STAR_RATINGS = ['1-star','2-star','3-star','4-star','5-star','5-star Deluxe','Heritage','Budget','Resort'];

const EMPTY_FORM = {
  vendor_name:'', vendor_type:'Hotel', star_rating:'', property_type:'',
  rooms:'', banquets:'', largest_indoor_venue:'', lawns:'', largest_lawns:'',
  address:'', area:'', landmark:'', city:'', billing_state:'', pincode:'', gstin:'',
  contacts: [{ hotel_no:'', contact_no:'', email:'', sale_person:'' }],
  hotel_no:'', contact_no:'', email:'', sale_person:'',
};

const inp = (err) => ({
  width:'100%', background: err ? '#FFF5F5' : '#F9FAFB',
  border: `1.5px solid ${err ? '#FCA5A5' : '#D1D5DB'}`,
  borderRadius:10, padding:'10px 14px',
  fontFamily:'Sora,sans-serif', fontSize:13, color:'#111827',
  outline:'none', transition:'border-color 0.2s', boxSizing:'border-box',
});
const lbl = {
  fontSize:11, fontWeight:700, letterSpacing:'0.08em',
  textTransform:'uppercase', color:'#6B7280', display:'block', marginBottom:5,
};
const fg = { display:'flex', flexDirection:'column', gap:4 };
const sectionHead = {
  fontSize:11, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase',
  color:'#16213E', background:'#EEF2F7', padding:'7px 14px', borderRadius:8,
  marginBottom:12, marginTop:4,
};

// ── Email format validator (used for hotel contact "Mail ID" rows) ────────────
function isValidEmail(email) {
  if (!email || !email.trim()) return true; // empty is fine — email is optional
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function buildContacts(vendor) {
  if (!vendor) return [{ hotel_no:'', contact_no:'', email:'', sale_person:'' }];
  const emails      = (vendor.email       || '').split('//').map(s => s.trim());
  const hotelNos    = (vendor.hotel_no    || '').split('//').map(s => s.trim());
  const contactNos  = (vendor.contact_no  || '').split('//').map(s => s.trim());
  const salePersons = (vendor.sale_person || '').split('//').map(s => s.trim());
  const count = Math.max(emails.length, hotelNos.length, contactNos.length, salePersons.length, 1);
  return Array.from({ length: count }, (_, i) => ({
    hotel_no:    hotelNos[i]    || '',
    contact_no:  contactNos[i]  || '',
    email:       emails[i]      || '',
    sale_person: salePersons[i] || '',
  }));
}

// ── Download helper (creates and clicks a link) ───────────────────────────────
function downloadCSV(filename, rows) {
  const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ── Room Type Template Download ───────────────────────────────────────────────
function downloadRoomTemplate() {
  const header = ['room_name', 'max_adults', 'max_children'];
  const sample = [
    ['Standard Room',          2, 1],
    ['Deluxe Room',            2, 1],
    ['Premium Deluxe Room',    2, 2],
    ['Junior Suite',           2, 2],
    ['Suite',                  3, 2],
    ['Executive Suite',        3, 2],
    ['Presidential Suite',     4, 2],
    ['Single Room',            1, 0],
    ['Triple Room',            3, 1],
    ['Family Room',            4, 2],
    ['Pool View Room',         2, 1],
    ['Sea View Room',          2, 1],
    ['Mountain View Room',     2, 1],
    ['Garden Room',            2, 1],
    ['Villa',                  4, 2],
    ['Pool Villa',             4, 2],
    ['Cottage',                2, 1],
  ];
  downloadCSV('room_types_template.csv', [header, ...sample]);
  toast.success('Template downloaded! Fill in your room types and upload.');
}

// ═══════════════════════════════════════════════════════════════════════════════
// BULK ROOM UPLOAD MODAL
// ═══════════════════════════════════════════════════════════════════════════════
class BulkRoomUploadModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedHotelId:   '',
      selectedHotelName: '',
      hotelSearch:       '',
      showHotelDrop:     false,
      file:              null,
      parsing:           false,
      preview:           null,   // { rooms: [{room_name, max_adults, max_children}] }
      uploading:         false,
      done:              false,
      insertedCount:     0,
    };
    this._fileRef = React.createRef();
    this._dropRef = React.createRef();
  }

  // Filter hotels by search
  filteredHotels() {
    const { hotels } = this.props;
    const q = this.state.hotelSearch.toLowerCase();
    if (!q) return hotels;
    return hotels.filter(h =>
      (h.vendor_name || '').toLowerCase().includes(q) ||
      (h.city || '').toLowerCase().includes(q)
    );
  }

  // Parse CSV/Excel file client-side
  async parseFile(file) {
    this.setState({ parsing: true, preview: null });
    try {
      const text = await file.text();
      const lines = text.trim().split('\n').filter(Boolean);
      if (lines.length < 2) { toast.error('File has no data rows'); this.setState({ parsing: false }); return; }

      const header = lines[0].split(',').map(h => h.replace(/"/g, '').trim().toLowerCase());
      const nameIdx     = header.findIndex(h => ['room_name','room name','name','room type','room_type'].includes(h));
      const adultsIdx   = header.findIndex(h => ['max_adults','max adults','adults','max adult'].includes(h));
      const childrenIdx = header.findIndex(h => ['max_children','max children','children','max child'].includes(h));

      if (nameIdx === -1) { toast.error('Could not find "room_name" column'); this.setState({ parsing: false }); return; }

      const rooms = lines.slice(1).map(line => {
        const cols = line.split(',').map(c => c.replace(/"/g, '').trim());
        return {
          room_name:    cols[nameIdx]     || '',
          max_adults:   parseInt(adultsIdx   >= 0 ? cols[adultsIdx]   : 2) || 2,
          max_children: parseInt(childrenIdx >= 0 ? cols[childrenIdx] : 1) || 1,
        };
      }).filter(r => r.room_name);

      if (!rooms.length) { toast.error('No valid room types found'); this.setState({ parsing: false }); return; }
      this.setState({ preview: { rooms }, parsing: false });
    } catch (e) {
      toast.error('Failed to parse file: ' + e.message);
      this.setState({ parsing: false });
    }
  }

  async handleUpload() {
    const { selectedHotelId, preview } = this.state;
    if (!selectedHotelId) { toast.error('Select a hotel first'); return; }
    if (!preview?.rooms?.length) { toast.error('No rooms to upload'); return; }

    this.setState({ uploading: true });
    try {
      await api.post(`/vendors/${selectedHotelId}/room-types`, { room_types: preview.rooms });
      this.setState({ done: true, insertedCount: preview.rooms.length });
      toast.success(`${preview.rooms.length} room types added!`);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Upload failed');
    } finally { this.setState({ uploading: false }); }
  }

  render() {
    const { onClose } = this.props;
    const { selectedHotelId, selectedHotelName, hotelSearch, showHotelDrop,
            file, parsing, preview, uploading, done, insertedCount } = this.state;
    const filtered = this.filteredHotels();

    return (
      <div style={{ position:'fixed', inset:0, zIndex:300, background:'rgba(0,0,0,0.50)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
        <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:580, maxHeight:'90vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 24px 64px rgba(0,0,0,0.22)' }}>

          {/* Header */}
          <div style={{ background:'#16213E', padding:'16px 22px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:36, height:36, borderRadius:10, background:'rgba(201,168,76,0.2)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <BedDouble size={18} color="#C9A84C" />
              </div>
              <div>
                <div style={{ fontSize:15, fontWeight:700, color:'#fff' }}>Bulk Room Upload</div>
                <div style={{ fontSize:11, color:'#B8C8E0', marginTop:1 }}>Select hotel → Upload CSV → Done</div>
              </div>
            </div>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:8, padding:6, cursor:'pointer', color:'#fff', display:'flex' }}><X size={18} /></button>
          </div>

          <div style={{ padding:'22px', overflowY:'auto', flex:1, display:'flex', flexDirection:'column', gap:20 }}>

            {/* ── Success ── */}
            {done && (
              <div style={{ textAlign:'center', padding:'20px 0' }}>
                <CheckCircle size={52} color="#0FA968" style={{ margin:'0 auto 16px' }} />
                <h3 style={{ fontSize:18, fontWeight:800, color:'#0B7A4C', marginBottom:6 }}>Rooms Added!</h3>
                <p style={{ fontSize:14, color:'#6B7280', marginBottom:20 }}>
                  <strong>{insertedCount}</strong> room types added to <strong>{selectedHotelName}</strong>
                </p>
                <button onClick={onClose} style={{ padding:'10px 28px', borderRadius:10, border:'none', background:'#16213E', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>Done</button>
              </div>
            )}

            {!done && (<>

              {/* Step 1: Hotel selector */}
              <div>
                <label style={{ ...lbl, marginBottom:8 }}>Step 1 — Select Hotel</label>
                <div style={{ position:'relative' }} ref={this._dropRef}>
                  <div
                    onClick={() => this.setState({ showHotelDrop: !showHotelDrop })}
                    style={{ display:'flex', alignItems:'center', justifyContent:'space-between', border:`1.5px solid ${selectedHotelId ? '#16213E' : '#D1D5DB'}`, borderRadius:10, padding:'10px 14px', cursor:'pointer', background:selectedHotelId ? '#F5F7FF' : '#F9FAFB', userSelect:'none' }}>
                    <span style={{ fontSize:13, color:selectedHotelId ? '#16213E' : '#9CA3AF', fontWeight:selectedHotelId ? 700 : 400 }}>
                      {selectedHotelName || 'Search and select a hotel…'}
                    </span>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      {selectedHotelId && (
                        <button type="button" onClick={e => { e.stopPropagation(); this.setState({ selectedHotelId:'', selectedHotelName:'', hotelSearch:'' }); }}
                          style={{ background:'none', border:'none', cursor:'pointer', color:'#9CA3AF', display:'flex', padding:0 }}><X size={13} /></button>
                      )}
                      <ChevronDown size={14} color="#9CA3AF" style={{ transform:showHotelDrop?'rotate(180deg)':'none', transition:'transform 0.15s' }} />
                    </div>
                  </div>

                  {showHotelDrop && (
                    <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, background:'#fff', border:'1.5px solid #E5E7EB', borderRadius:12, boxShadow:'0 8px 24px rgba(0,0,0,0.12)', zIndex:10, overflow:'hidden' }}>
                      <div style={{ padding:'8px 10px', borderBottom:'1px solid #F3F4F6' }}>
                        <div style={{ position:'relative' }}>
                          <Search size={13} style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color:'#9CA3AF' }} />
                          <input autoFocus value={hotelSearch} onChange={e => this.setState({ hotelSearch: e.target.value })}
                            placeholder="Type hotel name or city…"
                            style={{ width:'100%', paddingLeft:28, paddingRight:8, height:34, border:'1px solid #E5E7EB', borderRadius:8, fontSize:12, outline:'none', fontFamily:'Sora,sans-serif', boxSizing:'border-box' }} />
                        </div>
                      </div>
                      <div style={{ maxHeight:200, overflowY:'auto' }}>
                        {filtered.length === 0 ? (
                          <div style={{ padding:'16px', textAlign:'center', fontSize:13, color:'#9CA3AF' }}>No hotels found</div>
                        ) : filtered.map(h => (
                          <div key={h.id} onClick={() => this.setState({ selectedHotelId:h.id, selectedHotelName:h.vendor_name, showHotelDrop:false, hotelSearch:'' })}
                            style={{ padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid #F9FAFB', display:'flex', justifyContent:'space-between', alignItems:'center' }}
                            onMouseEnter={e => e.currentTarget.style.background='#F5F7FF'}
                            onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                            <div>
                              <div style={{ fontSize:13, fontWeight:600, color:'#111827' }}>{h.vendor_name}</div>
                              {h.city && <div style={{ fontSize:11, color:'#9CA3AF', marginTop:1 }}>{h.city}{h.billing_state ? `, ${h.billing_state}` : ''}</div>}
                            </div>
                            {h.star_rating && <span style={{ fontSize:10, color:'#FF6B4A', fontWeight:600 }}>{h.star_rating}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {selectedHotelId && (
                  <p style={{ fontSize:11, color:'#0FA968', marginTop:5, fontWeight:600 }}>✓ {selectedHotelName} selected</p>
                )}
              </div>

              {/* Step 2: Download template + Upload file */}
              <div style={{ opacity: selectedHotelId ? 1 : 0.4, pointerEvents: selectedHotelId ? 'auto' : 'none', transition:'opacity 0.2s' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                  <label style={lbl}>Step 2 — Upload Room CSV</label>
                  <button onClick={downloadRoomTemplate}
                    style={{ display:'flex', alignItems:'center', gap:5, padding:'5px 12px', border:'1.5px solid #16213E', borderRadius:8, background:'transparent', fontSize:11, fontWeight:700, color:'#16213E', cursor:'pointer' }}>
                    <Download size={12} /> Download Template
                  </button>
                </div>

                {/* Template columns info */}
                <div style={{ background:'#F5F7FF', border:'1px solid #C7D2FE', borderRadius:10, padding:'10px 14px', marginBottom:12 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:'#3730A3', marginBottom:6 }}>CSV columns expected:</div>
                  <div style={{ display:'flex', gap:8 }}>
                    {[{col:'room_name', note:'*required'}, {col:'max_adults', note:'default: 2'}, {col:'max_children', note:'default: 1'}].map(c => (
                      <div key={c.col} style={{ flex:1, background:'#fff', border:'1px solid #E0E7FF', borderRadius:8, padding:'8px 10px' }}>
                        <div style={{ fontFamily:'JetBrains Mono,monospace', fontSize:11, fontWeight:700, color:'#16213E' }}>{c.col}</div>
                        <div style={{ fontSize:10, color:'#6B7280', marginTop:2 }}>{c.note}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Drop zone */}
                {!preview && (
                  <div
                    onClick={() => this._fileRef.current?.click()}
                    onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor='#C9A84C'; e.currentTarget.style.background='#FFFBEB'; }}
                    onDragLeave={e => { e.currentTarget.style.borderColor='#D1D5DB'; e.currentTarget.style.background='#F9FAFB'; }}
                    onDrop={e => {
                      e.preventDefault(); e.currentTarget.style.borderColor='#D1D5DB'; e.currentTarget.style.background='#F9FAFB';
                      const f = e.dataTransfer.files[0];
                      if (f) { this.setState({ file:f }); this.parseFile(f); }
                    }}
                    style={{ border:'2px dashed #D1D5DB', borderRadius:14, padding:'30px 20px', textAlign:'center', cursor:'pointer', background:'#F9FAFB', transition:'all 0.15s' }}>
                    {parsing ? (
                      <div style={{ color:'#6B7280', fontSize:13, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                        <RefreshCw size={16} style={{ animation:'spin 1s linear infinite' }} /> Parsing…
                      </div>
                    ) : (
                      <>
                        <Upload size={28} color="#9CA3AF" style={{ margin:'0 auto 10px' }} />
                        <p style={{ fontSize:13, fontWeight:600, color:'#374151', marginBottom:3 }}>Drop CSV here or click to browse</p>
                        <p style={{ fontSize:11, color:'#9CA3AF' }}>.csv files only</p>
                        {file && <p style={{ fontSize:11, color:'#0FA968', marginTop:6, fontWeight:600 }}>📎 {file.name}</p>}
                      </>
                    )}
                    <input ref={this._fileRef} type="file" accept=".csv" style={{ display:'none' }}
                      onChange={e => { const f = e.target.files[0]; if (f) { this.setState({ file:f }); this.parseFile(f); } }} />
                  </div>
                )}

                {/* Preview */}
                {preview && (
                  <div>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                      <span style={{ fontSize:13, fontWeight:700, color:'#0FA968' }}>✓ {preview.rooms.length} room types ready to upload</span>
                      <button onClick={() => this.setState({ preview:null, file:null })}
                        style={{ fontSize:11, color:'#6B7280', background:'none', border:'none', cursor:'pointer', textDecoration:'underline' }}>
                        Change file
                      </button>
                    </div>
                    <div style={{ border:'1px solid #E5E7EB', borderRadius:12, overflow:'hidden', maxHeight:220, overflowY:'auto' }}>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                        <thead>
                          <tr style={{ background:'#16213E', position:'sticky', top:0 }}>
                            {['#','Room Name','Max Adults','Max Children'].map(h => (
                              <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:10, fontWeight:700, color:'#C9A84C', letterSpacing:'0.08em', textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {preview.rooms.map((r, i) => (
                            <tr key={i} style={{ background:i%2===0?'#fff':'#F9FAFB', borderBottom:'1px solid #F1F5F9' }}>
                              <td style={{ padding:'8px 12px', color:'#9CA3AF', fontFamily:'JetBrains Mono,monospace', fontSize:11 }}>{i+1}</td>
                              <td style={{ padding:'8px 12px', fontWeight:600, color:'#111827' }}>{r.room_name}</td>
                              <td style={{ padding:'8px 12px', color:'#2F5FE0', fontWeight:700, textAlign:'center' }}>{r.max_adults}</td>
                              <td style={{ padding:'8px 12px', color:'#7C3AED', fontWeight:700, textAlign:'center' }}>{r.max_children}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

            </>)}
          </div>

          {/* Footer */}
          {!done && (
            <div style={{ padding:'14px 22px', background:'#F9FAFB', borderTop:'1px solid #E5E7EB', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
              <button onClick={onClose} style={{ padding:'10px 18px', borderRadius:10, border:'1.5px solid #D1D5DB', background:'#fff', fontSize:13, fontWeight:600, color:'#374151', cursor:'pointer' }}>
                Cancel
              </button>
              <button onClick={() => this.handleUpload()} disabled={uploading || !selectedHotelId || !preview}
                style={{ padding:'10px 24px', borderRadius:10, border:'none', fontSize:13, fontWeight:700, color:'#fff', cursor:(!selectedHotelId||!preview||uploading)?'not-allowed':'pointer', background:(!selectedHotelId||!preview||uploading)?'#9CA3AF':'#0FA968', display:'flex', alignItems:'center', gap:7 }}>
                {uploading ? <><RefreshCw size={13} style={{ animation:'spin 1s linear infinite' }} /> Uploading…</> : <><BedDouble size={14} /> Upload {preview?.rooms?.length||0} Rooms</>}
              </button>
            </div>
          )}
        </div>
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }
}


// ═══════════════════════════════════════════════════════════════════════════════
// VENDOR MODAL
// ═══════════════════════════════════════════════════════════════════════════════
class VendorModal extends Component {
  constructor(props) {
    super(props);
    const vendor = props.vendor || null;
    this.state = {
      step:      0,
      form:      { ...EMPTY_FORM, ...(vendor||{}), gstin:(vendor?.gstin||''), contacts: buildContacts(vendor) },
      roomTypes: [],
      saving:    false,
      errors:    {},
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  async componentDidMount() {
    if (this.props.vendor?.id) {
      try {
        const { data } = await api.get(`/vendors/${this.props.vendor.id}/room-types`);
        this.setState({ roomTypes: data.data || [] });
      } catch (_) {}
    }
  }

  handleChange(e) {
    const { name, value } = e.target;
    this.setState(p => ({ form:{ ...p.form, [name]:value }, errors:{ ...p.errors, [name]:'' } }));
  }

  updateContact(i, key, val) {
    this.setState(p => {
      const contacts = [...p.form.contacts];
      contacts[i] = { ...contacts[i], [key]: val };

      // Live email validation — update/clear this row's error as they type
      const errors = { ...p.errors };
      if (key === 'email') {
        const emailErrors = { ...(errors.contact_emails || {}) };
        if (val && !isValidEmail(val)) {
          emailErrors[i] = 'Enter a valid email address';
        } else {
          delete emailErrors[i];
        }
        errors.contact_emails = emailErrors;
      }

      return { form: { ...p.form, contacts }, errors };
    });
  }
  addContact() {
    this.setState(p => ({
      form: { ...p.form, contacts: [...p.form.contacts, { hotel_no:'', contact_no:'', email:'', sale_person:'' }] }
    }));
  }
  removeContact(i) {
    this.setState(p => {
      const contacts = [...p.form.contacts];
      if (contacts.length <= 1) {
        contacts[0] = { hotel_no:'', contact_no:'', email:'', sale_person:'' };
      } else {
        contacts.splice(i, 1);
      }
      // Re-key contact_emails errors since indices shift after a removal
      const emailErrors = {};
      contacts.forEach((c, idx) => {
        if (c.email && !isValidEmail(c.email)) emailErrors[idx] = 'Enter a valid email address';
      });
      return { form: { ...p.form, contacts }, errors: { ...p.errors, contact_emails: emailErrors } };
    });
  }

  addRoomType()    { this.setState(p => ({ roomTypes:[...p.roomTypes, { room_name:'', max_adults:2, max_children:1 }] })); }
  removeRoomType(i){ this.setState(p => { const r=[...p.roomTypes]; r.splice(i,1); return { roomTypes:r }; }); }
  handleRoomChange(i, key, val) {
    this.setState(p => { const r=[...p.roomTypes]; r[i]={ ...r[i], [key]:val }; return { roomTypes:r }; });
  }

  validateStep0() {
    const errs = {};
    if (!this.state.form.vendor_name.trim()) errs.vendor_name = 'Hotel name is required';

    const contacts = this.state.form.contacts || [];
    const emailErrors = {};
    contacts.forEach((c, i) => {
      if (c.email && !isValidEmail(c.email)) emailErrors[i] = 'Enter a valid email address';
    });
    if (Object.keys(emailErrors).length > 0) errs.contact_emails = emailErrors;

    return errs;
  }

  handleNext() {
    const errs = this.validateStep0();
    if (Object.keys(errs).length) {
      this.setState({ errors: errs });
      if (errs.contact_emails) toast.error('Please fix the invalid email address(es) before continuing.');
      return;
    }
    this.setState({ step:1, errors:{} });
  }

  async handleSubmit() {
    const { form, roomTypes } = this.state;
    this.setState({ saving:true });
    try {
      const contacts    = form.contacts || [];
      const joinField   = (key) => contacts.map(c => (c[key]||'').trim()).filter(Boolean).join(' // ') || null;
      const saveForm = {
        ...form,
        hotel_no:    joinField('hotel_no'),
        contact_no:  joinField('contact_no'),
        email:       joinField('email'),
        sale_person: joinField('sale_person'),
      };
      delete saveForm.contacts;

      let vendorId;
      if (this.props.vendor) {
        await api.put(`/vendors/${this.props.vendor.id}`, saveForm);
        vendorId = this.props.vendor.id;
        toast.success('Hotel updated!');
      } else {
        const res = await api.post('/vendors', saveForm);
        vendorId = res.data.data?.id || res.data.id;
        toast.success('Hotel added!');
      }
      if (vendorId) {
        const validRooms = roomTypes.filter(r => r.room_name?.trim());
        await api.post(`/vendors/${vendorId}/room-types`, { room_types: validRooms });
      }
      this.props.onSaved({ id: vendorId, ...saveForm });
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { this.setState({ saving:false }); }
  }

  render() {
    const { onClose, vendor } = this.props;
    const { form, saving, errors, step, roomTypes } = this.state;
    const contacts = form.contacts || [{ hotel_no:'', contact_no:'', email:'', sale_person:'' }];

    const STEPS = [
      { label:'Hotel Details', icon:'🏨' },
      { label:'Room Types',    icon:'🛏️' },
    ];

    return (
      <div style={{ position:'fixed', inset:0, zIndex:200, background:'rgba(0,0,0,0.45)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
        <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:760, boxShadow:'0 24px 64px rgba(0,0,0,0.18)', overflow:'hidden', maxHeight:'93vh', display:'flex', flexDirection:'column' }}>

          <div style={{ background:'#16213E', padding:'18px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <Hotel size={18} color="#C9A84C" />
              <span style={{ fontSize:15, fontWeight:700, color:'#fff' }}>{vendor ? 'Edit Hotel' : 'Add New Hotel'}</span>
            </div>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:8, padding:6, cursor:'pointer', color:'#fff', display:'flex' }}>
              <X size={18} />
            </button>
          </div>

          <div style={{ padding:'14px 24px', background:'#FAFBFF', borderBottom:'1px solid #E5E7EB', flexShrink:0 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:0 }}>
              {STEPS.map((s, i) => (
                <React.Fragment key={i}>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                    <div onClick={() => i < step && this.setState({ step:i })}
                      style={{ width:36, height:36, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
                        fontSize:15, cursor: i < step ? 'pointer' : 'default',
                        background: i < step ? '#0FA968' : i === step ? '#16213E' : '#E5E7EB',
                        color: i <= step ? '#fff' : '#9CA3AF',
                        border: i === step ? '3px solid #C9A84C' : '3px solid transparent',
                      }}>
                      {i < step ? '✓' : s.icon}
                    </div>
                    <span style={{ fontSize:11, fontWeight: i===step?700:500, color: i===step?'#16213E': i<step?'#0FA968':'#9CA3AF', whiteSpace:'nowrap' }}>
                      {s.label}
                    </span>
                  </div>
                  {i < STEPS.length-1 && (
                    <div style={{ width:80, height:2, background: i<step?'#0FA968':'#E5E7EB', margin:'0 8px', marginTop:-20, transition:'background 0.3s' }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div style={{ padding:24, overflowY:'auto', flex:1 }}>
            {step === 0 && (
              <>
                <div style={sectionHead}>🏨 Basic Info</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
                  <div style={{ ...fg, gridColumn:'1 / -1' }}>
                    <label style={lbl}>Hotel Name *</label>
                    <input name="vendor_name" value={form.vendor_name} onChange={this.handleChange}
                      placeholder="e.g. Taj Lake Palace, Udaipur" style={inp(errors.vendor_name)} autoFocus />
                    {errors.vendor_name && <p style={{ fontSize:11, color:'#E1483A', margin:0 }}>{errors.vendor_name}</p>}
                  </div>
                  <div style={fg}>
                    <label style={lbl}>Star Rating</label>
                    <select name="star_rating" value={form.star_rating} onChange={this.handleChange} style={inp(false)}>
                      <option value="">Select rating</option>
                      {STAR_RATINGS.map(r=><option key={r}>{r}</option>)}
                    </select>
                  </div>
                  <div style={fg}>
                    <label style={lbl}>Property Type</label>
                    <select name="vendor_type" value={form.vendor_type} onChange={this.handleChange} style={inp(false)}>
                      {VENDOR_TYPES.map(t=><option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div style={fg}><label style={lbl}>Rooms</label><input type="number" name="rooms" value={form.rooms} onChange={this.handleChange} placeholder="Total rooms" style={inp(false)} min="0" /></div>
                  <div style={fg}><label style={lbl}>Banquets</label><input type="number" name="banquets" value={form.banquets} onChange={this.handleChange} placeholder="No. of banquet halls" style={inp(false)} min="0" /></div>
                  <div style={fg}><label style={lbl}>Largest Indoor Venue (pax)</label><input type="number" name="largest_indoor_venue" value={form.largest_indoor_venue} onChange={this.handleChange} placeholder="e.g. 500" style={inp(false)} min="0" /></div>
                  <div style={fg}><label style={lbl}>Lawns</label><input type="number" name="lawns" value={form.lawns} onChange={this.handleChange} placeholder="No. of lawns" style={inp(false)} min="0" /></div>
                  <div style={{ ...fg, gridColumn:'1 / -1' }}><label style={lbl}>Largest Lawns (pax)</label><input type="number" name="largest_lawns" value={form.largest_lawns} onChange={this.handleChange} placeholder="e.g. 800" style={inp(false)} min="0" /></div>
                </div>

                <div style={sectionHead}>📍 Location</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
                  <div style={{ ...fg, gridColumn:'1 / -1' }}>
                    <label style={lbl}>Address</label>
                    <textarea name="address" rows={2} value={form.address} onChange={this.handleChange} placeholder="Full street address..." style={{ ...inp(false), resize:'vertical' }} />
                  </div>
                  <div style={fg}><label style={lbl}>Area</label><input name="area" value={form.area} onChange={this.handleChange} placeholder="e.g. Fatehpura, Udaipur" style={inp(false)} /></div>
                  <div style={fg}><label style={lbl}>Landmark</label><input name="landmark" value={form.landmark} onChange={this.handleChange} placeholder="e.g. Near RK Circle" style={inp(false)} /></div>
                  <div style={fg}><label style={lbl}>City</label><input name="city" value={form.city} onChange={this.handleChange} placeholder="e.g. Udaipur" style={inp(false)} /></div>
                  <div style={fg}>
                    <label style={lbl}>State</label>
                    <select name="billing_state" value={form.billing_state} onChange={this.handleChange} style={inp(false)}>
                      <option value="">Select state</option>
                      {INDIAN_STATES.map(s=><option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div style={fg}><label style={lbl}>Pincode</label><input name="pincode" value={form.pincode} onChange={this.handleChange} placeholder="e.g. 313001" maxLength={6} style={inp(false)} /></div>
                  <div style={fg}>
                    <label style={lbl}>GSTIN</label>
                    <input name="gstin" value={form.gstin}
                      onChange={e => this.setState(p => ({ form:{ ...p.form, gstin:e.target.value.toUpperCase() } }))}
                      placeholder="27AAAAA0000A1Z5" style={inp(false)} />
                  </div>
                </div>

                <div style={sectionHead}>📞 Contact</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1.4fr 1fr 32px', gap:8, marginBottom:6, paddingLeft:2 }}>
                  {['Hotel No.','Contact No.','Mail ID','Sale Person',''].map((h,i)=>(
                    <span key={i} style={lbl}>{h}</span>
                  ))}
                </div>
                {contacts.map((c, ci) => {
                  const emailErr = errors.contact_emails?.[ci];
                  return (
                    <div key={ci} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1.4fr 1fr 32px', gap:8, marginBottom:8, alignItems:'start' }}>
                      <input value={c.hotel_no||''} placeholder="0294 288 8888" onChange={e => this.updateContact(ci,'hotel_no',e.target.value)} style={inp(false)} />
                      <input value={c.contact_no||''} placeholder="Mobile number" onChange={e => this.updateContact(ci,'contact_no',e.target.value)} style={inp(false)} />
                      <div style={fg}>
                        <input type="email" value={c.email||''} placeholder="reservations@hotel.com"
                          onChange={e => this.updateContact(ci,'email',e.target.value)} style={inp(emailErr)} />
                        {emailErr && <span style={{ fontSize:10, color:'#E1483A', fontWeight:600 }}>{emailErr}</span>}
                      </div>
                      <input value={c.sale_person||''} placeholder="Rahul Sharma" onChange={e => this.updateContact(ci,'sale_person',e.target.value)} style={inp(false)} />
                      <button type="button" onClick={() => this.removeContact(ci)}
                        style={{ width:32, height:38, background:'#FCEAE8', border:'1px solid #F5CAC5', borderRadius:8, cursor:'pointer', color:'#E1483A', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <X size={13} />
                      </button>
                    </div>
                  );
                })}
                <button type="button" onClick={() => this.addContact()}
                  style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:10, border:'1.5px dashed #2F5FE0', background:'#EFF6FF', fontSize:12, fontWeight:600, color:'#2F5FE0', cursor:'pointer' }}>
                  <Plus size={13} /> Add Contact Row
                </button>
              </>
            )}

            {step === 1 && (
              <div>
                <div style={{ marginBottom:16, padding:'10px 14px', background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:10, fontSize:13, color:'#1D4ED8' }}>
                  <strong>Step 2:</strong> Add room types for <strong>{form.vendor_name}</strong>
                  <span style={{ color:'#6B7280', fontWeight:400 }}> — optional, can be added later</span>
                </div>
                {roomTypes.length === 0 && (
                  <div style={{ textAlign:'center', padding:'30px 0', color:'#9CA3AF' }}>
                    <span style={{ fontSize:32 }}>🛏️</span>
                    <p style={{ fontSize:14, fontWeight:600, color:'#6B7280', marginTop:8 }}>No room types added yet</p>
                  </div>
                )}
                <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:16 }}>
                  {roomTypes.map((r, i) => (
                    <div key={i} style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr auto', gap:10, alignItems:'end', background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:12, padding:'14px 16px' }}>
                      <div style={fg}>
                        {i === 0 && <label style={lbl}>Room Name *</label>}
                        <input value={r.room_name} onChange={e => this.handleRoomChange(i,'room_name',e.target.value)} placeholder="e.g. Deluxe Room" style={inp(false)} />
                      </div>
                      <div style={fg}>
                        {i === 0 && <label style={lbl}>Max Adults</label>}
                        <input type="number" min="1" max="20" value={r.max_adults} onChange={e => this.handleRoomChange(i,'max_adults',parseInt(e.target.value)||1)} onFocus={e => e.target.select()} style={inp(false)} />
                      </div>
                      <div style={fg}>
                        {i === 0 && <label style={lbl}>Max Children</label>}
                        <input type="number" min="0" max="10" value={r.max_children} onChange={e => this.handleRoomChange(i,'max_children',parseInt(e.target.value)||0)} onFocus={e => e.target.select()} style={inp(false)} />
                      </div>
                      <button type="button" onClick={() => this.removeRoomType(i)}
                        style={{ background:'#FCEAE8', border:'1px solid #F5CAC5', borderRadius:8, padding:'9px 10px', cursor:'pointer', color:'#E1483A', display:'flex', alignSelf: i===0 ? 'flex-end' : 'center' }}>
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={() => this.addRoomType()}
                  style={{ display:'flex', alignItems:'center', gap:6, padding:'10px 18px', borderRadius:10, border:'1.5px dashed #2F5FE0', background:'#EFF6FF', fontSize:13, fontWeight:600, color:'#2F5FE0', cursor:'pointer', width:'100%', justifyContent:'center' }}>
                  <Plus size={14} /> Add Room Type
                </button>
              </div>
            )}
          </div>

          <div style={{ padding:'14px 24px', background:'#F9FAFB', borderTop:'1px solid #E5E7EB', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
            <button type="button" onClick={step===0 ? onClose : () => this.setState({ step:0 })}
              style={{ padding:'10px 18px', borderRadius:10, border:'1.5px solid #D1D5DB', background:'#fff', fontSize:13, fontWeight:600, color:'#374151', cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
              {step===0 ? 'Cancel' : '← Back'}
            </button>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              {STEPS.map((_,i) => (
                <div key={i} style={{ width: i===step?20:8, height:8, borderRadius:4, background: i<step?'#0FA968': i===step?'#16213E':'#D1D5DB', transition:'all 0.2s' }} />
              ))}
            </div>
            {step === 0 ? (
              <button type="button" onClick={() => this.handleNext()}
                style={{ padding:'10px 22px', borderRadius:10, border:'none', background:'#16213E', fontSize:13, fontWeight:700, color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
                Next →
              </button>
            ) : (
              <button type="button" onClick={this.handleSubmit} disabled={saving}
                style={{ padding:'10px 24px', borderRadius:10, border:'none', background:saving?'#9CA3AF':'#0FA968', fontSize:13, fontWeight:700, color:'#fff', cursor:saving?'not-allowed':'pointer', display:'flex', alignItems:'center', gap:7 }}>
                {saving ? <><RefreshCw size={13} style={{ animation:'spin 1s linear infinite' }} /> Saving…</> : <><Save size={14} /> {vendor ? 'Update Hotel' : 'Save Hotel'}</>}
              </button>
            )}
          </div>
        </div>
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }
}


// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
class HotelsPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      vendors:[], loading:true, search:'', typeFilter:'', stateFilter:'',
      showModal:false, editVendor:null, sortCol:'vendor_name', sortAsc:true,
      bulkModal: false, bulkFile: null, bulkParsing: false, bulkUploading: false,
      bulkPreview: null, bulkResult: null,
      // ── Room upload ──────────────────────────────────────────────────────────
      roomUploadModal: false,
    };
    this.fetchVendors    = this.fetchVendors.bind(this);
    this.handleToggle    = this.handleToggle.bind(this);
    this.handleDelete    = this.handleDelete.bind(this);
    this.handleSaved     = this.handleSaved.bind(this);
    this.handleBulkUpload = this.handleBulkUpload.bind(this);
    this._bulkFileRef    = React.createRef();
  }

  componentDidMount() {
    this.fetchVendors();
    if (!window.XLSX) {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
      document.head.appendChild(s);
    }
    // Arrived via the "Add Hotel" detour from an in-progress booking — open the
    // add-hotel modal right away so the user doesn't have to click it again.
    if (this.props.autoOpenAdd) this.setState({ showModal: true, editVendor: null });
  }
  componentDidUpdate(_, prev) {
    if (prev.typeFilter !== this.state.typeFilter || prev.stateFilter !== this.state.stateFilter)
      this.fetchVendors();
  }

  async fetchVendors() {
    this.setState({ loading:true });
    try {
      const params = {};
      if (this.state.typeFilter)  params.vendor_type = this.state.typeFilter;
      if (this.state.stateFilter) params.state       = this.state.stateFilter;
      if (this.state.search)      params.search      = this.state.search;
      const { data } = await api.get('/vendors', { params });
      this.setState({ vendors: data.data || [] });
    } catch { toast.error('Failed to load hotels'); }
    finally  { this.setState({ loading:false }); }
  }

  handleSearchChange = (e) => {
    this.setState({ search: e.target.value }, () => {
      clearTimeout(this._t);
      this._t = setTimeout(() => this.fetchVendors(), 400);
    });
  }

  async handleToggle(id) {
    try { const res = await api.patch(`/vendors/${id}/toggle`); toast.success(res.data.message); this.fetchVendors(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  }

  async handleDelete(id, name) {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await api.delete(`/vendors/${id}`); toast.success('Hotel deleted'); this.fetchVendors(); }
    catch (err) { toast.error(err.response?.data?.message || 'Cannot delete'); }
  }

  handleSaved(vendor) {
    this.setState({ showModal:false, editVendor:null });
    this.fetchVendors();
    // Arrived via the "Add Hotel" detour — jump back to the booking with the
    // newly created hotel ready to be auto-selected.
    if (this.props.onReturnToBooking) this.props.onReturnToBooking(vendor);
  }
  handleSort(col) { this.setState(p => ({ sortCol:col, sortAsc: p.sortCol===col ? !p.sortAsc : true })); }

  getSortedVendors() {
    const { vendors, sortCol, sortAsc } = this.state;
    return [...vendors].sort((a,b) => {
      const av = (a[sortCol]||'').toString().toLowerCase();
      const bv = (b[sortCol]||'').toString().toLowerCase();
      return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }

  async parseExcelFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const XLSX = window.XLSX;
          if (!XLSX) { reject(new Error('Excel library not loaded')); return; }
          const wb = XLSX.read(e.target.result, { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
          resolve(rows);
        } catch (err) { reject(err); }
      };
      reader.onerror = () => reject(new Error('File read failed'));
      reader.readAsArrayBuffer(file);
    });
  }

  async handleBulkPreview(file) {
    this.setState({ bulkParsing: true, bulkPreview: null, bulkResult: null });
    try {
      const rows = await this.parseExcelFile(file);
      if (!rows.length) { toast.error('No data rows found'); this.setState({ bulkParsing: false }); return; }
      const normalize = (row) => {
        const get = (...keys) => {
          for (const k of keys) {
            const found = Object.keys(row).find(rk => rk.trim().toLowerCase() === k.trim().toLowerCase());
            if (found !== undefined && String(row[found]).trim()) return String(row[found]).trim();
          }
          return '';
        };
        return {
          vendor_name: get('Hotels','Hotel','vendor_name','hotel_name','name'),
          vendor_type: get('Property Type','PropertyType','property_type','type'),
          star_rating: get('Star Rating','StarRating','star_rating','stars'),
          address:     get('Address','address'), area: get('Area','area'),
          landmark:    get('Landmark','landmark'), city: get('City','city'),
          billing_state: get('State','billing_state'),
          pincode:     get('Pincode','PinCode','pincode'),
          hotel_no:    get('Hotel No.','Hotel No','hotel_no'),
          contact_no:  get('Contact No.','contact_no'),
          email:       get('Mail ID','Email','email'),
          sale_person: get('Sale Person Name','sale_person'),
          rooms:       get('Rooms','rooms'), banquets: get('Banquets','banquets'),
          largest_indoor_venue: get('Largest Indoor Venue','largest_indoor_venue'),
          lawns: get('Lawns','lawns'), largest_lawns: get('Largest Lawns','largest_lawns'),
        };
      };
      const normalized = rows.map(normalize).filter(r => r.vendor_name);
      const makeKey = (r) => `${(r.vendor_name||'').toLowerCase().trim()}||${(r.city||'').toLowerCase().trim()}||${(r.address||'').toLowerCase().trim()}`;
      const existingKeys = new Set(this.state.vendors.map(v => makeKey(v)));
      const duplicates = normalized.filter(r => existingKeys.has(makeKey(r)));
      const newRows    = normalized.filter(r => !existingKeys.has(makeKey(r)));
      this.setState({ bulkPreview: { rows: normalized, duplicates, newRows }, bulkParsing: false });
    } catch (err) { toast.error('Failed to parse: ' + err.message); this.setState({ bulkParsing: false }); }
  }

  async handleBulkUpload() {
    const { bulkPreview } = this.state;
    if (!bulkPreview?.newRows?.length) { toast.error('No new records to insert'); return; }
    this.setState({ bulkUploading: true });
    try {
      const { data } = await api.post('/vendors/bulk', { hotels: bulkPreview.newRows });
      this.setState({ bulkResult: { inserted: data.inserted || bulkPreview.newRows.length, skipped: bulkPreview.duplicates.length }, bulkPreview: null, bulkFile: null });
      this.fetchVendors();
      toast.success(`${data.inserted || bulkPreview.newRows.length} hotels inserted!`);
    } catch (err) { toast.error(err.response?.data?.message || 'Bulk upload failed'); }
    finally { this.setState({ bulkUploading: false }); }
  }

  render() {
    const { loading, search, typeFilter, stateFilter, showModal, editVendor, sortCol, sortAsc, roomUploadModal } = this.state;
    const { user } = this.props;
    const role      = user?.role_name || '';
    const canWrite  = ['Admin', 'Manager', 'Staff'].includes(role);
    const canDelete = role === 'Admin';
    const sorted      = this.getSortedVendors();
    const activeCount = sorted.filter(v => v.is_active).length;

    const cols = [
      { key:'vendor_name',          label:'Hotel',                sortable:true,  width:180 },
      { key:'star_rating',          label:'Star Rating',          sortable:true,  width:100 },
      { key:'vendor_type',          label:'Property Type',        sortable:true,  width:100 },
      { key:'rooms',                label:'Rooms',                sortable:false, width:70  },
      { key:'banquets',             label:'Banquets',             sortable:false, width:80  },
      { key:'largest_indoor_venue', label:'Largest Indoor (pax)', sortable:false, width:110 },
      { key:'lawns',                label:'Lawns',                sortable:false, width:70  },
      { key:'largest_lawns',        label:'Largest Lawn (pax)',   sortable:false, width:100 },
      { key:'address',              label:'Address',              sortable:false, width:180 },
      { key:'area',                 label:'Area',                 sortable:false, width:120 },
      { key:'landmark',             label:'Landmark',             sortable:false, width:130 },
      { key:'city',                 label:'City',                 sortable:true,  width:90  },
      { key:'billing_state',        label:'State',                sortable:true,  width:100 },
      { key:'pincode',              label:'Pincode',              sortable:false, width:80  },
      { key:'gstin',                label:'GSTIN',                sortable:false, width:130 },
      { key:'is_active',            label:'Status',               sortable:false, width:80  },
      { key:'actions',              label:'Actions',              sortable:false, width: canWrite ? 110 : 60 },
    ];

    const SortIcon = ({ col }) => {
      if (sortCol !== col) return <span style={{ opacity:0.3, fontSize:10 }}>↕</span>;
      return sortAsc ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
    };

    const tdStyle = { padding:'11px 12px', fontSize:12, color:'#374151', borderBottom:'1px solid #F3F4F6', whiteSpace:'nowrap' };
    const tdMono  = { ...tdStyle, fontFamily:'JetBrains Mono,monospace' };
    const tdTrunc = (w) => ({ ...tdStyle, maxWidth:w, overflow:'hidden', textOverflow:'ellipsis' });

    return (
      <div>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
          <div>
            <h1 style={{ fontSize:26, fontWeight:800, color:'#16213E', letterSpacing:'-0.02em' }}>
              Hotel <span style={{ color:'#FF6B4A' }}>List</span>
            </h1>
            <p style={{ fontSize:13, color:'#6B7280', marginTop:3 }}>{sorted.length} hotels · {activeCount} active</p>
          </div>
          {canWrite && (
            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
              {/* ── Bulk Room Upload ── */}
              <button onClick={() => this.setState({ roomUploadModal: true })}
                style={{ display:'flex', alignItems:'center', gap:7, padding:'11px 18px', borderRadius:12, border:'1.5px solid #7C3AED', background:'#F5F3FF', color:'#7C3AED', fontSize:13, fontWeight:700, cursor:'pointer' }}>
                <BedDouble size={15} /> Bulk Room Upload
              </button>
              <button onClick={() => this.setState({ bulkModal:true, bulkPreview:null, bulkResult:null, bulkFile:null })}
                style={{ display:'flex', alignItems:'center', gap:7, padding:'11px 20px', borderRadius:12, border:'1.5px solid #16213E', background:'transparent', color:'#16213E', fontSize:13, fontWeight:700, cursor:'pointer' }}>
                <Upload size={15} /> Bulk Upload
              </button>
              <button onClick={() => this.setState({ showModal:true, editVendor:null })}
                style={{ display:'flex', alignItems:'center', gap:7, padding:'11px 22px', borderRadius:12, border:'none', background:'#FF6B4A', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', boxShadow:'0 4px 14px rgba(255,107,74,0.28)' }}>
                <Plus size={16} /> Add Hotel
              </button>
            </div>
          )}
        </div>

        {/* Stat cards */}
        <div style={{ display:'flex', gap:14, marginBottom:20, flexWrap:'wrap' }}>
          {[
            { label:'Total Hotels',     value:sorted.length,                          color:'#2F5FE0', Icon:Hotel       },
            { label:'Active',           value:activeCount,                            color:'#0FA968', Icon:ToggleRight },
            { label:'Inactive',         value:sorted.length-activeCount,              color:'#E1483A', Icon:ToggleLeft  },
            { label:'With Salesperson', value:sorted.filter(v=>v.sale_person).length, color:'#7C3AED', Icon:User        },
          ].map(s => (
            <div key={s.label} style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:14, padding:'14px 18px', display:'flex', alignItems:'center', gap:12, flex:1, minWidth:130, position:'relative', overflow:'hidden' }}>
              <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:s.color }} />
              <div style={{ width:36, height:36, borderRadius:10, background:`${s.color}18`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <s.Icon size={17} color={s.color} />
              </div>
              <div>
                <div style={{ fontSize:20, fontWeight:800, color:'#111827', fontFamily:'JetBrains Mono,monospace' }}>{s.value}</div>
                <div style={{ fontSize:10, color:'#9CA3AF', fontWeight:700, letterSpacing:'0.07em', textTransform:'uppercase' }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
          <div style={{ position:'relative', flex:1, minWidth:220 }}>
            <Search size={14} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#9CA3AF' }} />
            <input type="text" value={search} onChange={this.handleSearchChange} placeholder="Search hotel, city, salesperson…"
              style={{ width:'100%', paddingLeft:36, paddingRight:14, paddingTop:9, paddingBottom:9, border:'1.5px solid #D1D5DB', borderRadius:10, fontFamily:'Sora,sans-serif', fontSize:13, color:'#111827', background:'#fff', outline:'none' }} />
          </div>
          <select value={typeFilter} onChange={e => this.setState({ typeFilter:e.target.value })}
            style={{ background:'#fff', border:'1.5px solid #D1D5DB', borderRadius:10, padding:'9px 14px', fontFamily:'Sora,sans-serif', fontSize:13, color:'#111827', cursor:'pointer', outline:'none' }}>
            <option value="">All Types</option>
            {VENDOR_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
          <select value={stateFilter} onChange={e => this.setState({ stateFilter:e.target.value })}
            style={{ background:'#fff', border:'1.5px solid #D1D5DB', borderRadius:10, padding:'9px 14px', fontFamily:'Sora,sans-serif', fontSize:13, color:'#111827', cursor:'pointer', outline:'none' }}>
            <option value="">All States</option>
            {INDIAN_STATES.map(s => <option key={s}>{s}</option>)}
          </select>
          <button onClick={this.fetchVendors}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 16px', borderRadius:10, border:'1.5px solid #D1D5DB', background:'#fff', fontSize:13, fontWeight:600, color:'#374151', cursor:'pointer' }}>
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        {/* Table */}
        <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:18, boxShadow:'0 1px 6px rgba(0,0,0,0.05)', overflow:'hidden', display:'flex', flexDirection:'column' }}>
          {loading ? (
            <div style={{ textAlign:'center', padding:'60px 20px', color:'#9CA3AF' }}>
              <RefreshCw size={24} style={{ animation:'spin 1s linear infinite', marginBottom:12 }} />
              <p style={{ fontSize:14 }}>Loading hotels…</p>
            </div>
          ) : sorted.length === 0 ? (
            <div style={{ textAlign:'center', padding:'60px 20px' }}>
              <Hotel size={36} style={{ color:'#D1D5DB', marginBottom:14 }} />
              <p style={{ fontSize:15, fontWeight:600, color:'#374151' }}>No hotels found</p>
            </div>
          ) : (
            <div style={{ overflowX:'auto', overflowY:'auto', height:'calc(100vh - 330px)', minHeight:300, WebkitOverflowScrolling:'touch' }}>
              <table style={{ width:'max-content', minWidth:'100%', borderCollapse:'separate', borderSpacing:0 }}>
                <thead>
                  <tr>
                    <th style={{ padding:'11px 16px', textAlign:'left', fontSize:10, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'#9CA3AF', borderBottom:'1.5px solid #E5E7EB', background:'#FAFBFF', whiteSpace:'nowrap', position:'sticky', top:0, left:0, zIndex:4 }}>#</th>
                    {cols.map(c => (
                      <th key={c.key} onClick={c.sortable ? () => this.handleSort(c.key) : undefined}
                        style={{ padding:'11px 12px', textAlign:'left', fontSize:10, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:sortCol===c.key?'#16213E':'#9CA3AF', borderBottom:'1.5px solid #E5E7EB', background:'#FAFBFF', whiteSpace:'nowrap', cursor:c.sortable?'pointer':'default', minWidth:c.width, userSelect:'none', position:'sticky', top:0, zIndex:2 }}>
                        <span style={{ display:'flex', alignItems:'center', gap:4 }}>{c.label}{c.sortable && <SortIcon col={c.key} />}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((v, i) => {
                    const rowBg = i%2===0 ? '#fff' : '#FAFBFF';
                    return (
                      <tr key={v.id} style={{ background:rowBg }}
                        onMouseEnter={e => e.currentTarget.style.background='#F0F4FF'}
                        onMouseLeave={e => e.currentTarget.style.background=rowBg}>
                        <td style={{ ...tdMono, color:'#9CA3AF', fontSize:11, position:'sticky', left:0, zIndex:1, background: i%2===0?'#fff':'#FAFBFF', boxShadow:'2px 0 5px rgba(0,0,0,0.06)' }}>#{v.id}</td>
                        <td style={{ padding:'11px 12px', borderBottom:'1px solid #F3F4F6', minWidth:180 }}>
                          <div style={{ fontWeight:700, color:'#111827', fontSize:13 }}>{v.vendor_name}</div>
                        </td>
                        <td style={tdStyle}>{v.star_rating ? (() => {
                          const r = v.star_rating;
                          const colorMap = { '1-star':{bg:'#F3F4F6',color:'#6B7280',border:'#D1D5DB'},'2-star':{bg:'#FEF9C3',color:'#713F12',border:'#FDE68A'},'3-star':{bg:'#DBEAFE',color:'#1E40AF',border:'#BFDBFE'},'4-star':{bg:'#FEF3C7',color:'#92400E',border:'#FCD34D'},'5-star':{bg:'#E4F9EE',color:'#0B7A4C',border:'#6EE7B7'},'5-star Deluxe':{bg:'#D1FAE5',color:'#064E3B',border:'#34D399'},'Heritage':{bg:'#FFF7ED',color:'#7C2D12',border:'#FDBA74'},'Budget':{bg:'#F9FAFB',color:'#374151',border:'#E5E7EB'},'Resort':{bg:'#EEF2FF',color:'#3730A3',border:'#A5B4FC'} };
                          const c = colorMap[r] || { bg:'#F3F4F6', color:'#374151', border:'#D1D5DB' };
                          return <span style={{ fontSize:11, padding:'2px 8px', borderRadius:10, background:c.bg, color:c.color, border:`1px solid ${c.border}`, whiteSpace:'nowrap', fontWeight:600 }}>{r}</span>;
                        })() : '—'}</td>
                        <td style={tdStyle}>{v.vendor_type !== 'Hotel' ? <span style={{ fontSize:11, padding:'2px 7px', borderRadius:10, background:'#EFF6FF', color:'#1D4ED8', border:'1px solid #BFDBFE' }}>{v.vendor_type}</span> : v.vendor_type}</td>
                        <td style={{ ...tdMono, textAlign:'center' }}>{v.rooms || '—'}</td>
                        <td style={{ ...tdMono, textAlign:'center' }}>{v.banquets || '—'}</td>
                        <td style={{ ...tdMono, textAlign:'center' }}>{v.largest_indoor_venue || '—'}</td>
                        <td style={{ ...tdMono, textAlign:'center' }}>{v.lawns || '—'}</td>
                        <td style={{ ...tdMono, textAlign:'center' }}>{v.largest_lawns || '—'}</td>
                        <td style={tdTrunc(180)} title={v.address}>{v.address || '—'}</td>
                        <td style={tdTrunc(120)} title={v.area}>{v.area || '—'}</td>
                        <td style={tdTrunc(130)} title={v.landmark}>{v.landmark || '—'}</td>
                        <td style={tdStyle}>{v.city || '—'}</td>
                        <td style={tdStyle}>{v.billing_state || '—'}</td>
                        <td style={tdMono}>{v.pincode || '—'}</td>
                        <td style={{ ...tdMono, fontSize:11, color:'#6B7280' }}>{v.gstin || '—'}</td>
                        <td style={{ padding:'11px 12px', borderBottom:'1px solid #F3F4F6' }}>
                          <span style={{ padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:600, background:v.is_active?'#E4F9EE':'#FCEAE8', color:v.is_active?'#0B7A4C':'#B5382D', border:`1px solid ${v.is_active?'#BEEED9':'#F5CAC5'}` }}>
                            {v.is_active ? '● Active' : '○ Inactive'}
                          </span>
                        </td>
                        <td style={{ padding:'11px 12px', borderBottom:'1px solid #F3F4F6' }}>
                          <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                            {canWrite ? (
                              <button title="Edit" onClick={() => this.setState({ showModal:true, editVendor:v })}
                                style={{ width:28, height:28, borderRadius:8, border:'1px solid #BFDBFE', background:'#EFF6FF', color:'#1D4ED8', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
                                <Edit2 size={12} />
                              </button>
                            ) : (
                              <span style={{ fontSize:10, background:'#F3F4F6', color:'#9CA3AF', borderRadius:20, padding:'2px 8px', fontWeight:600, whiteSpace:'nowrap' }}>View Only</span>
                            )}
                            {canDelete && (<>
                              <button title={v.is_active ? 'Deactivate' : 'Activate'} onClick={() => this.handleToggle(v.id)}
                                style={{ width:28, height:28, borderRadius:8, border:`1px solid ${v.is_active?'#FDE68A':'#BBF7D0'}`, background:v.is_active?'#FFFBEB':'#F0FDF4', color:v.is_active?'#92400E':'#166534', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
                                {v.is_active ? <ToggleLeft size={12} /> : <ToggleRight size={12} />}
                              </button>
                              <button title="Delete" onClick={() => this.handleDelete(v.id, v.vendor_name)}
                                style={{ width:28, height:28, borderRadius:8, border:'1px solid #F5CAC5', background:'#FCEAE8', color:'#E1483A', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
                                <Trash2 size={12} />
                              </button>
                            </>)}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && sorted.length > 0 && (
          <p style={{ fontSize:12, color:'#9CA3AF', marginTop:10, textAlign:'right' }}>
            Showing {sorted.length} hotel{sorted.length!==1?'s':''}
          </p>
        )}

        {!canWrite && (
          <div style={{ marginTop:16, padding:'12px 18px', borderRadius:12, background:'#FFFBEB', border:'1px solid #FDE68A', display:'flex', alignItems:'center', gap:10, fontSize:13, color:'#92400E' }}>
            <Shield size={15} /> You have view-only access.
          </div>
        )}

        {showModal && <VendorModal vendor={editVendor} onClose={() => this.setState({ showModal:false, editVendor:null })} onSaved={this.handleSaved} />}

        {/* Bulk Room Upload Modal */}
        {roomUploadModal && (
          <BulkRoomUploadModal
            hotels={sorted.filter(v => v.is_active)}
            onClose={() => this.setState({ roomUploadModal: false })} />
        )}

        {/* Bulk Hotel Upload Modal */}
        {this.state.bulkModal && (() => {
          const { bulkFile, bulkParsing, bulkUploading, bulkPreview, bulkResult } = this.state;
          const close = () => this.setState({ bulkModal:false, bulkPreview:null, bulkResult:null, bulkFile:null });
          return (
            <div style={{ position:'fixed', inset:0, zIndex:300, background:'rgba(0,0,0,0.55)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
              <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:680, maxHeight:'92vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 24px 64px rgba(0,0,0,0.22)' }}>
                <div style={{ background:'#16213E', padding:'16px 22px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:34, height:34, borderRadius:10, background:'rgba(201,168,76,0.2)', display:'flex', alignItems:'center', justifyContent:'center' }}><FileSpreadsheet size={17} color="#C9A84C" /></div>
                    <div><div style={{ fontSize:15, fontWeight:700, color:'#fff' }}>Bulk Upload Hotels</div><div style={{ fontSize:11, color:'#B8C8E0', marginTop:1 }}>Duplicates are skipped automatically</div></div>
                  </div>
                  <button onClick={close} style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:8, padding:6, cursor:'pointer', color:'#fff', display:'flex' }}><X size={18} /></button>
                </div>
                <div style={{ padding:'20px 22px', overflowY:'auto', flex:1 }}>
                  {bulkResult && (
                    <div style={{ textAlign:'center', padding:'30px 20px' }}>
                      <CheckCircle size={48} color="#0FA968" style={{ margin:'0 auto 16px' }} />
                      <h3 style={{ fontSize:18, fontWeight:700, color:'#0B7A4C', marginBottom:8 }}>Upload Complete!</h3>
                      <div style={{ display:'flex', gap:16, justifyContent:'center', marginBottom:20 }}>
                        <div style={{ background:'#E4F9EE', border:'1px solid #BEEED9', borderRadius:12, padding:'12px 28px', textAlign:'center' }}><div style={{ fontSize:28, fontWeight:800, color:'#0B7A4C' }}>{bulkResult.inserted}</div><div style={{ fontSize:12, color:'#6B7280', marginTop:4 }}>Hotels Inserted</div></div>
                        <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:12, padding:'12px 28px', textAlign:'center' }}><div style={{ fontSize:28, fontWeight:800, color:'#92400E' }}>{bulkResult.skipped}</div><div style={{ fontSize:12, color:'#6B7280', marginTop:4 }}>Skipped (Duplicates)</div></div>
                      </div>
                      <button onClick={close} style={{ padding:'10px 28px', borderRadius:10, border:'none', background:'#16213E', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>Done</button>
                    </div>
                  )}
                  {!bulkResult && !bulkPreview && (
                    <>
                      <div style={{ background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:12, padding:'14px 18px', marginBottom:18 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:'#1E40AF', marginBottom:6 }}>Expected Excel columns</div>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                          {['vendor_name *','vendor_type','city','billing_state','email','hotel_no','address','star_rating','sale_person','gst_no'].map(c => (
                            <span key={c} style={{ fontSize:11, background:c.includes('*')?'#DBEAFE':'#F3F4F6', color:c.includes('*')?'#1E40AF':'#374151', border:`1px solid ${c.includes('*')?'#BFDBFE':'#E5E7EB'}`, borderRadius:6, padding:'2px 8px', fontFamily:'monospace', fontWeight:c.includes('*')?700:400 }}>{c}</span>
                          ))}
                        </div>
                      </div>
                      <div onClick={() => this._bulkFileRef.current?.click()} onDragOver={e=>{e.preventDefault();e.currentTarget.style.borderColor='#C9A84C';e.currentTarget.style.background='#FFFBEB';}} onDragLeave={e=>{e.preventDefault();e.currentTarget.style.borderColor='#D1D5DB';e.currentTarget.style.background='#F9FAFB';}} onDrop={e=>{e.preventDefault();e.currentTarget.style.borderColor='#D1D5DB';e.currentTarget.style.background='#F9FAFB';const f=e.dataTransfer.files[0];if(f){this.setState({bulkFile:f});this.handleBulkPreview(f);}}}
                        style={{ border:'2px dashed #D1D5DB', borderRadius:14, padding:'36px 20px', textAlign:'center', cursor:'pointer', background:'#F9FAFB', transition:'all 0.15s' }}>
                        <Upload size={32} color="#9CA3AF" style={{ margin:'0 auto 12px' }} />
                        <p style={{ fontSize:14, fontWeight:600, color:'#374151', marginBottom:4 }}>Drop your Excel file here or click to browse</p>
                        <p style={{ fontSize:12, color:'#9CA3AF' }}>.xlsx or .xls files only</p>
                        {bulkFile && <p style={{ fontSize:12, color:'#0FA968', marginTop:8, fontWeight:600 }}>📎 {bulkFile.name}</p>}
                        <input ref={this._bulkFileRef} type="file" accept=".xlsx,.xls" style={{ display:'none' }} onChange={e=>{const f=e.target.files[0];if(f){this.setState({bulkFile:f});this.handleBulkPreview(f);}}} />
                      </div>
                      {bulkParsing && <div style={{ textAlign:'center', padding:'20px', color:'#6B7280', fontSize:13, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}><RefreshCw size={16} style={{ animation:'spin 1s linear infinite' }} /> Parsing file…</div>}
                    </>
                  )}
                  {bulkPreview && !bulkResult && (
                    <>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:18 }}>
                        <div style={{ background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:12, padding:'12px 16px', textAlign:'center' }}><div style={{ fontSize:22, fontWeight:800, color:'#1E40AF' }}>{bulkPreview.rows.length}</div><div style={{ fontSize:11, color:'#6B7280', marginTop:3 }}>Total Rows</div></div>
                        <div style={{ background:'#E4F9EE', border:'1px solid #BEEED9', borderRadius:12, padding:'12px 16px', textAlign:'center' }}><div style={{ fontSize:22, fontWeight:800, color:'#0B7A4C' }}>{bulkPreview.newRows.length}</div><div style={{ fontSize:11, color:'#6B7280', marginTop:3 }}>New (will be inserted)</div></div>
                        <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:12, padding:'12px 16px', textAlign:'center' }}><div style={{ fontSize:22, fontWeight:800, color:'#92400E' }}>{bulkPreview.duplicates.length}</div><div style={{ fontSize:11, color:'#6B7280', marginTop:3 }}>Duplicates (skipped)</div></div>
                      </div>
                      <div style={{ border:'1px solid #E5E7EB', borderRadius:12, overflow:'hidden', marginBottom:12 }}>
                        <div style={{ background:'#F9FAFB', padding:'8px 14px', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.07em', borderBottom:'1px solid #E5E7EB' }}>Preview — {bulkPreview.rows.length} records</div>
                        <div style={{ overflowX:'auto', maxHeight:260 }}>
                          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                            <thead><tr style={{ background:'#F3F4F6' }}>{['Status','Hotel Name','City','Address','State','Phone'].map(h=><th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:600, color:'#374151', borderBottom:'1px solid #E5E7EB', whiteSpace:'nowrap' }}>{h}</th>)}</tr></thead>
                            <tbody>
                              {bulkPreview.rows.map((row,i)=>{const isDup=bulkPreview.duplicates.some(d=>d.vendor_name===row.vendor_name);return(
                                <tr key={i} style={{ background:isDup?'#FFFBEB':i%2===0?'#fff':'#F9FAFB', borderBottom:'1px solid #F1F5F9' }}>
                                  <td style={{ padding:'7px 12px' }}><span style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:20, border:'1px solid', background:isDup?'#FEF3C7':'#E4F9EE', color:isDup?'#92400E':'#0B7A4C', borderColor:isDup?'#FDE68A':'#BEEED9' }}>{isDup?'⏭ Skip':'✚ New'}</span></td>
                                  <td style={{ padding:'7px 12px', fontWeight:600, color:'#111827' }}>{row.vendor_name}</td>
                                  <td style={{ padding:'7px 12px', color:'#6B7280' }}>{row.city||'—'}</td>
                                  <td style={{ padding:'7px 12px', color:'#6B7280', maxWidth:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={row.address}>{row.address||'—'}</td>
                                  <td style={{ padding:'7px 12px', color:'#6B7280' }}>{row.billing_state||'—'}</td>
                                  <td style={{ padding:'7px 12px', color:'#6B7280' }}>{row.hotel_no||'—'}</td>
                                </tr>
                              );})}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                {bulkPreview && !bulkResult && (
                  <div style={{ padding:'14px 22px', background:'#F9FAFB', borderTop:'1px solid #E5E7EB', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
                    <button onClick={() => this.setState({ bulkPreview:null, bulkFile:null })} style={{ padding:'10px 18px', borderRadius:10, border:'1.5px solid #D1D5DB', background:'#fff', fontSize:13, fontWeight:600, color:'#374151', cursor:'pointer' }}>← Change File</button>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <span style={{ fontSize:12, color:'#6B7280' }}>{bulkPreview.newRows.length} new records</span>
                      <button onClick={this.handleBulkUpload} disabled={bulkUploading||bulkPreview.newRows.length===0}
                        style={{ padding:'10px 24px', borderRadius:10, border:'none', fontSize:13, fontWeight:700, color:'#fff', display:'flex', alignItems:'center', gap:8, cursor:bulkUploading||bulkPreview.newRows.length===0?'not-allowed':'pointer', background:bulkUploading||bulkPreview.newRows.length===0?'#9CA3AF':'#0FA968' }}>
                        {bulkUploading?<><RefreshCw size={13} style={{ animation:'spin 1s linear infinite' }} /> Uploading…</>:<><Upload size={13} /> Insert {bulkPreview.newRows.length} Hotels</>}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }
}

export default HotelsPage;